import { useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { isValidNip, normalizeNip } from '../utils/validation';
import api from '../services/api';
import { transactionService } from '../services/transactionService';

/**
 * Hook do przetwarzania platnosci w POS
 * Zawiera logike walidacji i finalizacji transakcji
 */
const usePaymentProcessing = ({
  // Stan koszyka
  cart,
  getFinalTotal,
  getEffectiveDiscount,
  getTotalAmount,
  appliedDiscounts,
  clearCart,
  setCurrentTransaction,
  currentTransaction,
  priceLimitSettings,
  priceLimitWarnings,

  // Stan platnosci
  paymentMethod,
  documentType,
  skipWz,

  // Stan zmiany
  currentShift,
  setShowCloseShiftModal,

  // Stan klienta
  selectedCustomer,

  // Stan lokalizacji
  currentLocationId,

  // Modale
  setShowCashPaymentModal,
  setCashReceived,
  setCashChange,
  setShowCouponPaymentModal,

  // Loading/error
  setLoading,
  setError,

  // Druk fiskalny
  autoPrintReceipt,
  printFiscalReceipt,

  // Statystyki
  loadDailyStats,
  loadSalesTarget,
  // Reset stanow po transakcji
  setEditingPrices,
  setAppliedDiscounts,
  // Opcjonalne: aktualizacja sumy rabatu po korekcie serwera (regula grosza)
  setCartDiscountTotal,
  setProductSearchQuery,
  setSelectedCategory,
  setSelectedCustomer,
  setCouponCode,
  resetCouponPaymentModal,
  resetSplitPayments,
  onTransactionComplete
}) => {

  // Guard przeciw podwojnemu kliknieciu — useRef nie triggeruje re-renderow
  const processingRef = useRef(false);

  // Czy w tej sprzedazy wykonano juz automatyczna korekte rabatu po odmowie
  // serwera (RABAT_REGULA_GROSZA). Maksymalnie JEDNA automatyczna korekta -
  // drugi 400 z rzedu oznacza, ze cos jest naprawde nie tak i kasjerka
  // dostaje instrukcje zdjecia rabatu zamiast kolejnej cichej proby.
  const rabatSkorygowanyRef = useRef(false);

  /**
   * Obsluga ODMOWY finalizacji z ustrukturyzowanym bledem serwera (kontrakt Z2).
   *
   * Ustalenie D wlasciciela: gdy cokolwiek sie nie zgadza - sprzedaz stoi
   * i kasjerka dostaje komunikat co zrobic, ale NIGDY petle 400 bez wyjscia.
   * Serwer przy odmowie RABAT_REGULA_GROSZA ma kwote poprawna JUZ zapisana
   * w bazie i odsyla ja w rabat_poprawny (+ do_zaplaty), wiec kasa przyjmuje
   * te kwote, aktualizuje rabat i pozwala kasjerce zatwierdzic ponownie -
   * druga proba przechodzi.
   */
  const obsluzBladFinalizacji = useCallback((completeResponse) => {
    const errData = completeResponse?.errorData || {};

    if (errData.error_code === 'RABAT_REGULA_GROSZA'
        && errData.rabat_poprawny !== undefined && errData.rabat_poprawny !== null) {
      const poprawny = Math.round(parseFloat(errData.rabat_poprawny) * 100) / 100;
      const doZaplaty = errData.do_zaplaty !== undefined && errData.do_zaplaty !== null
        ? ` Do zaplaty: ${parseFloat(errData.do_zaplaty).toFixed(2)} zl.` : '';

      if (!rabatSkorygowanyRef.current) {
        rabatSkorygowanyRef.current = true;
        // Serwer zapisal juz kwote poprawna - rabat na kasie staje sie
        // kwotowy rowny tej kwocie, wiec silnik rabatu odtworzy JA SAMA
        // przy nastepnym zatwierdzeniu (discount_amount == rabat_poprawny).
        if (setAppliedDiscounts) {
          setAppliedDiscounts(prev => {
            const lista = Array.isArray(prev) ? prev : [];
            if (lista.length === 0 || !lista[0]) return lista;
            return lista.map((d, i) => i === 0
              ? { ...d, typ_rabatu: 'kwotowy', wartosc: poprawny, kwota_rabatu: poprawny }
              : d);
          });
        }
        if (setCartDiscountTotal) setCartDiscountTotal(poprawny);
        toast.error(
          `Rabat zostal obnizony do ${poprawny.toFixed(2)} zl `
          + `(pozycja paragonu nie moze kosztowac 0.00 zl).${doZaplaty} `
          + 'Sprawdz kwote i zatwierdz platnosc ponownie.',
          { duration: 15000 }
        );
      } else {
        toast.error(
          'Rabat nadal nie przechodzi mimo korekty. Usun rabat z koszyka '
          + '(przycisk przy rabacie), sprawdz pozycje i nadaj rabat ponownie. '
          + 'Jesli problem wraca - zglos to kierownikowi.',
          { duration: 20000 }
        );
      }
      return;
    }

    if (errData.error_code === 'PLATNOSC_NIEZGODNA'
        && errData.do_zaplaty !== undefined && errData.do_zaplaty !== null) {
      // Rozbicie platnosci nie zgadza sie z kwota do zaplaty - serwer odsyla
      // kwote poprawna; komunikat serwera juz ja zawiera wraz z instrukcja.
      toast.error(
        (completeResponse.error || 'Suma platnosci nie zgadza sie z kwota do zaplaty.')
        + ` Popraw rozbicie platnosci na ${parseFloat(errData.do_zaplaty).toFixed(2)} zl.`,
        { duration: 15000 }
      );
      return;
    }

    // === BLEDY KUPONOWE FINALIZACJI (kontrakt K1: W1/W3/W4) ===
    // Serwer odmawia czytelnym stringiem (bez error_code) i sprzedaz jest
    // wycofana W CALOSCI (brak paragonu, brak KP). Tlumaczymy odmowe na
    // jezyk kasjerki i mowimy co zrobic dalej.
    const opis = typeof completeResponse.error === 'string' ? completeResponse.error : '';

    if (opis.indexOf('wymaga kodu kuponu') !== -1) {
      // W3: platnosc kuponem bez kodu - twarde 400 z serwera.
      rabatSkorygowanyRef.current = false;
      toast.error(
        'Nie podano kodu kuponu. Zeskanuj karte albo wpisz kod i zatwierdz '
        + 'platnosc jeszcze raz - sprzedaz NIE zostala zamknieta.',
        { duration: 15000 }
      );
      // Przy pojedynczej platnosci kuponem od razu otworz okno na kod.
      if (paymentMethod === 'kupon' && setShowCouponPaymentModal) {
        setShowCouponPaymentModal(true);
      }
      return;
    }

    if (opis.indexOf('nie mogl zostac wykorzystany') !== -1) {
      // W1: przegrana atomowa konsumpcja - najczesciej ten sam kupon zostal
      // PRZED CHWILA zuzyty na innej kasie (wyscig rozstrzyga blokada wiersza).
      rabatSkorygowanyRef.current = false;
      toast.error(
        opis + ' Najczesciej znaczy to, ze kupon zostal przed chwila uzyty '
        + 'na innej kasie - sprawdz jego saldo w module Kupony.',
        { duration: 20000 }
      );
      return;
    }

    if (opis.indexOf('Kupon') === 0) {
      // Pre-walidacja kuponow (status / data waznosci / pokrycie kwoty).
      rabatSkorygowanyRef.current = false;
      const dopisek = opis.indexOf('wykorzystany') !== -1
        ? ' Ten kupon zostal juz zuzyty - byc moze przed chwila na innej '
          + 'kasie. Przyjmij zaplate inna metoda.'
        : ' Sprzedaz NIE zostala zamknieta - popraw platnosc albo przyjmij '
          + 'zaplate inna metoda.';
      toast.error(opis + dopisek, { duration: 20000 });
      return;
    }

    // Inny blad - korekta rabatu nie jest w toku, wyzeruj licznik prob.
    rabatSkorygowanyRef.current = false;
    toast.error(completeResponse.error || "Blad finalizacji transakcji");
  }, [setAppliedDiscounts, setCartDiscountTotal, paymentMethod, setShowCouponPaymentModal]);

  /**
   * Walidacja przed platnoscia
   */
  const validatePayment = useCallback(() => {
    // Sprawdz czy zmiana jest przeterminowana
    if (currentShift?.is_expired) {
      toast.error("Zmiana jest przeterminowana! Zamknij stara zmiane i otworz nowa przed dokonaniem transakcji.");
      setShowCloseShiftModal(true);
      return false;
    }

    // Sprawdz czy zmiana jest otwarta
    if (!currentShift) {
      toast.error("Musisz otworzyc zmiane kasowa przed dokonaniem transakcji.");
      return false;
    }

    if (cart.length === 0) {
      toast.error("Koszyk jest pusty");
      return false;
    }

    // Blokada granic ceny przy finalizacji
    if (priceLimitSettings && priceLimitSettings.enabled) {
      const invalidItems = Object.keys(priceLimitWarnings);
      if (invalidItems.length > 0) {
        const itemNames = invalidItems.map(id => {
          const item = cart.find(i => String(i.id) === String(id));
          return item ? `${item.name || item.nazwa}: ${priceLimitWarnings[id].message}` : null;
        }).filter(Boolean).join(', ');

        if (!priceLimitSettings.allow_override_with_permission) {
          toast.error(`GRANICA CENY! Produkty ponizej minimalnej marzy: ${itemNames}. Popraw ceny przed finalizacja.`);
          return false;
        } else {
          const proceed = window.confirm(
            `OSTRZEZENIE: Granica ceny!\n\nProdukty ponizej minimalnej marzy:\n\n${itemNames}\n\nCzy na pewno chcesz kontynuowac? (wymaga uprawnien administratora)`
          );
          if (!proceed) return false;
        }
      }
    }

    // Dla faktury firma wymagamy klienta z NIP
    if (documentType === 'faktura_firma') {
      if (!selectedCustomer) {
        toast.error("Dla faktury firmowej musisz wybrac klienta!");
        return false;
      }
      const nipValue = selectedCustomer.nip;
      const hasNip = nipValue && typeof nipValue === 'string' && nipValue.trim() !== '';
      if (!hasNip) {
        toast.error("Dla faktury firmowej klient musi miec NIP! Edytuj klienta i dodaj NIP.");
        return false;
      }
    }

    // Dla paragonu z NIP wymagamy klienta z NIP
    if (documentType === 'paragon_nip') {
      if (!selectedCustomer) {
        toast.error("Dla paragonu z NIP musisz najpierw wybrac klienta!");
        return false;
      }
      const nipValue = selectedCustomer.nip;
      const hasNip = nipValue && typeof nipValue === 'string' && nipValue.trim() !== '';
      if (!hasNip) {
        toast.error("Dla paragonu z NIP klient musi miec NIP! Edytuj klienta i dodaj NIP.");
        return false;
      }
      // Suma kontrolna NIP (ta sama regula co CustomerModal i serwer:
      // pos_transaction_service.sprawdz_nabywce_paragonu_nip) - literowka
      // w NIP-ie nie moze trafic na drukarke fiskalna.
      if (!isValidNip(nipValue)) {
        toast.error(
          `NIP ${normalizeNip(nipValue)} jest bledny (zla suma kontrolna) - popraw NIP w karcie klienta.`,
          { duration: 12000 }
        );
        return false;
      }

      // Limit 450 zl brutto dla paragonu z NIP (faktura uproszczona). Rowno 450,00 zl
      // przechodzi, kazda wyzsza kwota blokuje. Serwer sprawdza to samo przy finalizacji
      // (pos_transaction_service.sprawdz_limit_paragonu_nip) - tu tylko szybka informacja.
      const totalAmount = Math.round(getFinalTotal() * 100) / 100;
      const LIMIT_PARAGON_NIP = 450;
      if (totalAmount > LIMIT_PARAGON_NIP) {
        toast.error(
          `Kwota ${totalAmount.toFixed(2)} zł przekroczyła 450 zł - nie możemy wystawić paragonu z NIP. `
          + 'Rozbij sprzedaż na dwa lub więcej paragonów, tak aby każdy z nich miał nie więcej niż 450 zł.',
          { duration: 12000 }
        );
        return false;
      }
    }

    // Dla faktury imiennej wymagamy klienta (dane osobowe)
    if (documentType === 'faktura_imienna') {
      if (!selectedCustomer) {
        toast.error("Dla faktury imiennej musisz wybrac klienta!");
        return false;
      }
    }

    if (!currentLocationId) {
      toast.error("Najpierw wybierz lokalizacje w prawym gornym rogu!");
      return false;
    }

    return true;
  }, [
    cart, currentShift, priceLimitSettings, priceLimitWarnings,
    documentType, selectedCustomer, currentLocationId, getFinalTotal,
    setShowCloseShiftModal
  ]);

  /**
   * Synchronizacja koszyka z backendem
   */
  // Ref żeby syncCartWithBackend zawsze miał aktualny cart
  const cartRef = useRef(cart);
  cartRef.current = cart;

  const syncCartWithBackend = useCallback(async (transactionId) => {
    const currentCart = cartRef.current;
    try {
      await api.delete(`/pos/cart/${transactionId}/items/clear`);
    } catch (e) {
      console.error("Nie udalo sie wyczyscic pozycji:", e);
    }

    // KAUCJE: zrodlem prawdy jest produkt-rodzic (parent.deposit_data + parent.quantity),
    // NIE oddzielne deposit cart items. Race condition w addToCart moze gubic
    // deposit items z UI cart, ale parent.quantity zawsze jest poprawne.
    // Dlatego: ignorujemy is_deposit items z cart, sumujemy kaucje per (kaucja_product_id, price)
    // na podstawie parents.deposit_data.
    const parents = currentCart.filter(it =>
      !(it.is_deposit === true || it.stawka_vat === -2 || it.typ_produktu === 'kaucja')
    );

    // Wyslij produkty rodzice
    for (const item of parents) {
      try {
        await api.post(`/pos/cart/${transactionId}/items`, {
          product_id: item.id,
          ilosc: item.quantity,
          cena_jednostkowa: item.price,
        });
      } catch (e) {
        console.error("Blad dodawania produktu do transakcji:", item.name, e);
      }
    }

    // Zsumuj kaucje per (kaucja_product_id, price) z parents.deposit_data
    const depositTotals = new Map();
    for (const parent of parents) {
      const dd = parent.deposit_data;
      if (!dd || !dd.kaucja_product_id) continue;
      const key = `${dd.kaucja_product_id}|${Number(dd.kaucja_price).toFixed(2)}`;
      const prev = depositTotals.get(key);
      if (prev) {
        prev.quantity += parent.quantity;
      } else {
        depositTotals.set(key, {
          product_id: dd.kaucja_product_id,
          price: dd.kaucja_price,
          quantity: parent.quantity,
        });
      }
    }

    // Wyslij kaucje (jeden POST per grupa)
    for (const dep of depositTotals.values()) {
      try {
        await api.post(`/pos/cart/${transactionId}/items`, {
          product_id: dep.product_id,
          ilosc: dep.quantity,
          cena_jednostkowa: dep.price,
        });
      } catch (e) {
        console.error("Blad dodawania kaucji do transakcji:", dep.product_id, e);
      }
    }
  }, []);

  /**
   * Budowanie danych platnosci
   */
  const buildPaymentData = useCallback((receivedAmount, splitPaymentsData, couponData) => {
    const finalAmount = getFinalTotal();
    const receivedAmountFloat = receivedAmount ? parseFloat(receivedAmount) : finalAmount;
    const changeAmount = paymentMethod === "gotowka" ? Math.max(0, receivedAmountFloat - finalAmount) : 0;

    // Lista ID produktow wykluczonych z rabatu
    const excludedFromDiscountIds = cart
      .filter(item => item.wykluczony_z_rabatow === 1 || item.wykluczony_z_rabatow === true)
      .map(item => item.id);

    return {
      payment_method: splitPaymentsData ? "dzielona" : paymentMethod,
      amount_paid: finalAmount,
      kwota_otrzymana: receivedAmountFloat,
      amount_change: changeAmount,
      customer_id: selectedCustomer?.id || null,
      document_type: documentType,
      discount_amount: getEffectiveDiscount(),
      excluded_from_discount: excludedFromDiscountIds,
      ...(splitPaymentsData && {
        split_payments: splitPaymentsData.filter(p => p.amount > 0).map(p => ({
          method: p.method,
          amount: p.amount,
          // Kod kanoniczny (bez spacji, wielkie litery) - tak trzyma go serwer
          ...(p.method === 'kupon' && p.couponCode && { coupon_code: p.couponCode.trim().toUpperCase() })
        }))
      }),
      ...(couponData && {
        coupon_code: couponData.code
      }),
      skip_wz: skipWz || false,
    };
  }, [cart, paymentMethod, documentType, selectedCustomer, skipWz, getFinalTotal, getEffectiveDiscount]);

  /**
   * Budowanie danych transakcji (dla nowej transakcji)
   */
  const buildTransactionData = useCallback((splitPaymentsData, couponData) => {
    return {
      customer_id: selectedCustomer?.id || null,
      type: "sale",
      location_id: currentLocationId,
      document_type: documentType,
      items: cart.map((item) => ({
        product_id: item.id,
        quantity: item.quantity,
        price: item.price,
        total: item.price * item.quantity,
      })),
      payment_method: splitPaymentsData ? "dzielona" : paymentMethod,
      total_amount: getFinalTotal(),
      total_gross: getTotalAmount(),
      discount_amount: getEffectiveDiscount(),
      applied_discounts: appliedDiscounts,
      cashier: currentShift?.kasjer_login || "admin",
      ...(splitPaymentsData && {
        split_payments: splitPaymentsData.filter(p => p.amount > 0).map(p => ({
          method: p.method,
          amount: p.amount,
          // Kod kanoniczny (bez spacji, wielkie litery) - tak trzyma go serwer
          ...(p.method === 'kupon' && p.couponCode && { coupon_code: p.couponCode.trim().toUpperCase() })
        }))
      }),
      ...(couponData && {
        coupon_code: couponData.code,
        coupon_value: couponData.value,
        coupon_discount: Math.min(couponData.value, getFinalTotal())
      }),
      skip_wz: skipWz || false,
    };
  }, [
    cart, paymentMethod, documentType, selectedCustomer, currentLocationId,
    currentShift, appliedDiscounts, skipWz, getFinalTotal, getTotalAmount, getEffectiveDiscount
  ]);

  /**
   * Drukowanie paragonu fiskalnego.
   *
   * KWOTA NA PARAGONIE = KWOTA Z ODPOWIEDZI FINALIZE (ustalenie A wlasciciela:
   * kwota pobrana od klienta i kwota na paragonie to MA BYC ta sama liczba).
   * Serwer w odpowiedzi finalize odsyla suma_brutto i rabat_kwota takie,
   * jakie ZAPISAL w pos_transakcje - druk nie moze miec wlasnego zrodla
   * kwoty. getFinalTotal() zostaje tylko jako awaryjny fallback, gdy
   * odpowiedz nie niesie kwot (stary format).
   */
  const handleFiscalPrint = useCallback(async (transactionId, receiptNumber, splitPaymentsData, finalizeMsg = null) => {
    if (autoPrintReceipt && (documentType === 'paragon' || documentType === 'paragon_nip' || documentType === 'faktura_imienna')) {
      const cartCopy = [...cart];
      let totalAmount = getFinalTotal();
      if (finalizeMsg && finalizeMsg.suma_brutto !== undefined && finalizeMsg.suma_brutto !== null) {
        const sumaBrutto = parseFloat(finalizeMsg.suma_brutto);
        const rabatKwota = parseFloat(finalizeMsg.rabat_kwota) || 0;
        if (Number.isFinite(sumaBrutto)) {
          totalAmount = Math.round((sumaBrutto - rabatKwota) * 100) / 100;
        }
      }
      const cashierName = currentShift?.kasjer_login || '';
      const customerNip = documentType === 'paragon_nip' ? selectedCustomer?.nip : null;
      // WAŻNE: Przekazujemy paymentMethod bezpośrednio (nie przez closure wrappera)
      await printFiscalReceipt(cartCopy, totalAmount, cashierName, transactionId, customerNip, splitPaymentsData, receiptNumber, paymentMethod);
    }
  }, [cart, documentType, currentShift, selectedCustomer, autoPrintReceipt, getFinalTotal, printFiscalReceipt, paymentMethod]);

  /**
   * Wyswietlenie komunikatu sukcesu
   */
  const showSuccessMessage = useCallback((invoiceNumber = null, fromOrder = false) => {
    if (documentType === 'paragon') {
      const msg = fromOrder
        ? "Paragon zostal pomyslnie zarejestrowany! Zamowienie oznaczone jako zrealizowane."
        : "Paragon zostal pomyslnie zarejestrowany!";
      toast.success(msg);
    } else if (documentType === 'paragon_nip') {
      toast.success(`Paragon z NIP nabywcy zostal pomyslnie zarejestrowany! NIP: ${selectedCustomer?.nip || ''}`);
    } else if (documentType === 'faktura_imienna') {
      toast.success("Paragon zarejestrowany! Faktura imienna zostanie utworzona na zadanie.");
    } else if (documentType === 'faktura_firma') {
      toast.success(`Faktura VAT zostala wystawiona${invoiceNumber ? `: ${invoiceNumber}` : ''}!`);
    }
  }, [documentType, selectedCustomer]);

  /**
   * Finalizacja po sukcesie
   */
  const handleSuccess = useCallback(async (transactionId) => {
    console.log('✅ handleSuccess wywołane, resetuję stany...');
    // Powiadom o ukończeniu transakcji (przed wyczyszczeniem koszyka)
    if (onTransactionComplete) {
      try { onTransactionComplete(cart); } catch (e) { console.debug('onTransactionComplete error:', e); }
    }
    setCurrentTransaction(transactionId ? { id: transactionId } : null);
    clearCart();
    // Reset stanow edycji po udanej transakcji
    if (setEditingPrices) setEditingPrices({});
    if (setAppliedDiscounts) setAppliedDiscounts([]); // appliedDiscounts to tablica (uzywana z .filter), nie obiekt
    if (setProductSearchQuery) setProductSearchQuery('');
    if (setSelectedCategory) setSelectedCategory('');
    if (setSelectedCustomer) {
      console.log('✅ Resetuję selectedCustomer na null');
      setSelectedCustomer(null);
    } else {
      console.log('⚠️ setSelectedCustomer nie jest zdefiniowane!');
    }
    if (setCouponCode) setCouponCode('');
    if (resetCouponPaymentModal) resetCouponPaymentModal();
    if (resetSplitPayments) resetSplitPayments();
    await loadDailyStats();
    await loadSalesTarget();
    console.log('✅ handleSuccess zakończone');
  }, [setCurrentTransaction, clearCart, loadDailyStats, loadSalesTarget, setEditingPrices, setAppliedDiscounts, setProductSearchQuery, setSelectedCategory, setSelectedCustomer, setCouponCode, resetCouponPaymentModal, resetSplitPayments, onTransactionComplete, cart]);

  /**
   * Glowna funkcja przetwarzania platnosci
   */
  const processPayment = useCallback(async (receivedAmount = null, couponData = null, splitPaymentsData = null) => {
    // Guard: zapobiegaj podwojnemu wyslaniu (double-click)
    if (processingRef.current) {
      return;
    }
    processingRef.current = true;

    // Walidacja
    if (!validatePayment()) {
      processingRef.current = false;
      return;
    }

    // Jesli platnosc gotowkowa (ale nie dzielona) i nie podano kwoty otrzymanej, otworz modal
    if (!splitPaymentsData && paymentMethod === "gotowka" && receivedAmount === null) {
      processingRef.current = false;
      // Reset wartosci przed otwarciem modala
      setCashReceived('');
      setCashChange(0);
      setShowCashPaymentModal(true);
      return;
    }
    // Jesli platnosc kuponem (ale nie dzielona) i nie podano danych kuponu
    // ALBO dane kuponu nie maja kodu - otworz modal. W3: bez kodu nie ma
    // platnosci kuponem, wiec finalizacja bez niego i tak dostanie 400.
    else if (!splitPaymentsData && paymentMethod === "kupon" && (!couponData || !couponData.code)) {
      processingRef.current = false;
      setShowCouponPaymentModal(true);
      return;
    }

    setLoading(true);
    try {
      // Sprawdz czy jest juz istniejaca transakcja (np. z zamowienia)
      const existingTransactionId = currentTransaction?.id;

      if (existingTransactionId) {
        // Uzyj istniejacej transakcji z zamowienia - nie tworz nowej!

        // Synchronizacja koszyka z backendem
        await syncCartWithBackend(existingTransactionId);

        // Przygotuj dane platnosci
        const paymentData = buildPaymentData(receivedAmount, splitPaymentsData, couponData);

        // Finalizuj transakcje
        const completeResponse = await transactionService.completeTransaction(
          existingTransactionId,
          paymentData
        );

        if (completeResponse.success) {
          rabatSkorygowanyRef.current = false;
          const finalizeMsg = completeResponse.data?.message;
          const receiptNumber = finalizeMsg?.numer_paragonu;
          await handleFiscalPrint(existingTransactionId, receiptNumber, splitPaymentsData, finalizeMsg);
          await handleSuccess(null);
          showSuccessMessage(completeResponse.data?.invoice_number, true);
        } else {
          obsluzBladFinalizacji(completeResponse);
        }
        return;
      }

      // Brak istniejacej transakcji - utworz nowa
      const transactionData = buildTransactionData(splitPaymentsData, couponData);

      // Utworz transakcje przez transactionService
      const response = await transactionService.createTransaction(transactionData);

      if (response.success) {
        const transactionId = response.data.transaction_id;
        const transactionStatus = response.data.status;

        // Audyt (podwojna dedukcja stanu): id transakcji zapamietujemy
        // NATYCHMIAST po utworzeniu koszyka, nie dopiero w handleSuccess.
        // Gdy finalizacja odmowi (np. kupon zuzyty przez inna kase) albo
        // padnie siec PO zdjeciu stanu, retry kasjerki idzie trasa
        // istniejacej transakcji (syncCart + finalize tego samego koszyka
        // - idempotentna, stan nie schodzi drugi raz), zamiast tworzyc
        // NOWY koszyk i zdejmowac stan/FIFO ponownie. Czyszczone w
        // handleSuccess po udanej sprzedazy.
        setCurrentTransaction({ id: transactionId });

        // Sprawdz czy transakcja jest juz sfinalizowana
        if (transactionStatus === "zakonczony") {
          const receiptNumber = response.data.receipt_number;
          await handleFiscalPrint(transactionId, receiptNumber, splitPaymentsData);
          await handleSuccess(transactionId);
          showSuccessMessage(response.data?.invoice_number);
        } else {
          // Transakcja wymaga finalizacji
          const paymentData = buildPaymentData(receivedAmount, splitPaymentsData, couponData);

          const completeResponse = await transactionService.completeTransaction(
            transactionId,
            paymentData
          );

          if (completeResponse.success) {
            rabatSkorygowanyRef.current = false;
            const finalizeMsg = completeResponse.data?.message;
            const receiptNumber = finalizeMsg?.numer_paragonu;
            await handleFiscalPrint(transactionId, receiptNumber, splitPaymentsData, finalizeMsg);
            await handleSuccess(transactionId);
            showSuccessMessage(completeResponse.data?.invoice_number);
          } else {
            obsluzBladFinalizacji(completeResponse);
          }
        }
      } else {
        toast.error(response.error || "Blad tworzenia transakcji");
      }
    } catch (err) {
      toast.error("Blad przetwarzania platnosci: " + err.message);
    } finally {
      processingRef.current = false;
      setLoading(false);
    }
  }, [
    cart, paymentMethod, documentType, currentTransaction, setCurrentTransaction,
    validatePayment, syncCartWithBackend, buildPaymentData, buildTransactionData,
    handleFiscalPrint, handleSuccess, showSuccessMessage, obsluzBladFinalizacji,
    setShowCashPaymentModal, setShowCouponPaymentModal, setLoading,
    setCashReceived, setCashChange
  ]);

  return {
    processPayment,
    validatePayment
  };
};

export default usePaymentProcessing;
